import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { VideoEnvironmentProps } from '../../cvp-webgames-core/Components/Environments/React/VideoEnvironment';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import BasePracticeScenario from '../base-practice-scenario/BasePracticeScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { SuccessType } from '../general/general-scenario-stuff';
import { Windows } from '../../misc/tools';
import { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';
import BaseTestScenario from '../base-test-scenario/BaseTestScenario';

export default class RuthMcKenzie extends BaseTestScenario {
	public eTTCorrectOne: boolean; //larygnoscope
	public eTTCorrectTwo: boolean; // Suction
	public eTTCorrectThree: boolean; //Size
	public eTTCorrectFour: boolean; //All three good
	
	public ettDL: boolean;
	public ettVL: boolean;	  

	public get maxScore(): number {
		return 54600;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 500; 
		var setupFiberOpticsPoints = 1000;
		var cricPoints = 3900;
		var correctAttempt = 32700;

		return assessPatientRemoveDenturesPoints + 
					setupFiberOpticsPoints +
					cricPoints +
					correctAttempt;
    }

	public get totalNumberOfQuestions(): number {
		return 5;
	}

	public get currentLevel(): number {
		return 10;
    }

	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 30,
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 90,
			bloodPressureDenominator: 45,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 92,
			heartRate: 140,
			heartRateMax: 150,
			respirationRate: 28,
			resetEtc02: false,
			etc02: 35,
			showETC02: false,
			isApneaTimerIdle: true,
            onInductionTimerRanOut: () => {
                context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
				this._CheckOxy();
                context.failedScenario = true;
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time for Ruth McKenzie. She has expired. Review the debrief and then try again', true);
			}
		}, context.sideMenuId);
	}
	
	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);
		
		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				'When intubating a morbidly obese patient, a video laryngoscope is more likely to be successful than direct laryngoscopy.',
				'Morbidly obese patients are more likely to have difficult bag valve mask ventilation.',
				'Morbidly obese patients have a faster rate of desaturation.',
				'Did you factor in the clinical condition of the patient in your utilization of the Vortex approach?'
			],
			link: 'https://google.com'
		};
	}
	
	public getDebriefVortex(): DebriefVortex {
		return {
			successItems: [
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)',
				'SGA: ' + this.sgaAttemptCount + ' attempt(s)',
				'Cricothyrotomy: ' + this.cricAttemptCount + ' attempt(s)'				
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	

	public onBVMFinished() : void  {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onETTFinished() : void  {
		if(this.eTTCorrectOne && this.eTTCorrectTwo && this.eTTCorrectThree) {
			this.addScore(this.predeterminedPoints);
			this._CheckOxy();
			this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.ETT,'You established a stable, patent airway for Ruth McKenzie by using a flexible bronchoscope to intubate.  Ms. McKenzie was apneic for ' + this.apneaTimeElapsed + ' while you managed her airway. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. Review the debrief for a summary of key learning points.','Good Job on this Scenario');
		} else{
			this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
		}
		// Must use video larygnoscope <<< pending
		// Must use suction
		// Use size 7.0 or 8.0
	}

	public onSGAFinished() : void  {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onCricFinished() : void  {
		this._CheckOxy();
		this.showFailedScenarioPopUpAfterAuscultate(VortexUICurrentAttempt.Cric, 'You should not have attempted a cricothyrotomy. A stable, patent airway can be established through non-surgical attempts. Review the debrief and then try again."', 'Failed Scenario');
	}

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenation: You did not pre-oxygenate the patient.") // will probably get an additional text description
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}
	}

	public getAssessAirwayData() : AssessAirwayActionData[]  {
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'She has a large neck circumference. There is 3 to 4 cm of subcutaneous tissue, which makes it difficult to feel her neck. It is also difficult to locate or palpate thyroid and cricoid cartilage.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'Thyromental Distance: 4 finger breadths (6 cm)<br />Hyoid to Thyroid Notch Distance: 2 finger breadths (3 cm)'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'You see the patient moving her chin to her chest.'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'The patient doesn’t have dentures.',
				noPoints:true
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me',
				text: 'Yes'
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				imgUrl: './assets/images/assess-airway-images/Class2.png',
				title: '"Open mouth',
				text: 'Inter-incisor Distance: 3 finger breadths (4 cm)',
			}
		]
    }

	private _setupIntro () {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			Patient name is Ruth McKenzie. She’s a 45-year-old female, 
		// 			67 in, 130 kg. Her K is 4.6 and she has a full stomach. She 
		// 			has severe sleep apnea, diabetes, and hypertension. She now 
		// 			comes in for a ruptured gall bladder with acute abdomen. She 
		// 			has delirium due to sepsis.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario9 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DrCaleb,
		// 			text: `
		// 			She has an 18 gauge IV and was pulse oximetry 86% on room air before 
		// 			we put her on the 100% non-rebreather mask.
		// 			I’m already administering epinephrine and steroids. I’ll manage the
		// 			hemodynamics; you manage the airway.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario9 - intro - 53'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `Ruth McKenzie came in with a ruptured gall bladder with acute abdomen. She has delirium due to sepsis.<br />
		// 		Auscultation: Lung sounds distant but clear. 
		// 		`,
		// 		b: `
		// 			Age: 45<br />
		// 			Height (in): 67 in<br />
		// 			Weight (kg): 130 kg<br />
		// 			Potassium (K): 4.6<br />
		// 			NPO Status: She ate two hours ago<br />
		// 			<br />
		// 			PMH: Severe Obstructive Sleep Apnea (OSA) and BMI is 48; diabetes HTN.
		// 		`,
		// 		a: `She needs a patent airway.`,
		// 		r: `She needs to be intubated for ventilator support and management of her sepsis.`
		// 	}
		// }

		this._id_ScenarioSet(9); // <<<<<< CRITICAL: must be set in every scenario. This number does not always match the "level number" variable set in the declarations above, use the documented level numbers and not how they are listed in game <<<<<<<<


		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario9 - intro - 53'
				}
			],
			sbarConfig: {
				s: `Ruth McKenzie came in with a ruptured gall bladder with acute abdomen. She has delirium due to sepsis.<br />
				Auscultation: Lung sounds distant but clear. 
				`,
				b: `
					Age: 45<br />
					Height (in): 67 in<br />
					Weight (kg): 130 kg<br />
					Potassium (K): 4.6<br />
					NPO Status: She ate two hours ago<br />
					<br />
					PMH: Severe Obstructive Sleep Apnea (OSA) and BMI is 48; diabetes HTN.
				`,
				b508: `
					Age. 45.
					Height. 67 inches.
					Weight. 130 kilograms.
					Potassium. 4.6.
					NPO Status. She ate two hours ago.
					Past Medical History. Severe Obstructive Sleep Apnea and BMI is 48 diabetes HTN.
				`,
				a: `She needs a patent airway.`,
				r: `She needs to be intubated for ventilator support and management of her sepsis.`
			}
		}
	}

	private _setupGeneralNeutralState() {
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario9 - Flat-rebreather-loop - 54',
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader');
			}
		};

		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,	
				constants.toolbagTools.cric //comment this out on cric not required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,
				constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			toolsToChooseFrom: toolsArr,
			onTeamNotDebriefed: () => {
				this.debriefLogFailedItem("Brief other staff in the room about attempt order: Briefing your staff on the attempt order will help them anticipate providing the right equipment. Briefings empower your staff to notify you if you fall into fixation error. ")
			},
			onTeamDebriefed: () => {
				this.debriefLog('Briefed other staff in the room about attempt order');
			},
			onToolsOrganized: () => {
				this.debriefLog('Organized equipment');
			}
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario9 - Flat-rebreather-loop - 54',
			onSuctionSet: () => {
				this.debriefLog("Suction set");
			}
		};

		this.neutralState.config.part4Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario9 - 45 - Rebreather - 11',
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj; // <<<<<<<
				this.debriefLog("Bed Adjusted");
			}
		};

		this.neutralState.config.part5Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario9 - 45 - patient-assessment-up - 36',
			chinToChestVideoChapter: () =>'DAARC - Scenario9 - 45 - patient-assessment-down - 37',
			examineNeckVideoChapter: () =>'DAARC - Scenario9 - 45 - patient-assessment-straight - 35',
			realTeethVideoChapter: () =>'DAARC - Scenario9 - 45 - patient-assessment-straight - 35',
			stickOutTongueVideoChapter: () =>'DAARC - Scenario9 - 45 - patient-assessment-straight - 35',
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment")
			}
		};

		this.neutralState.config.part6Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			waitForFiberOpticVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onSetupFiberOptic: () => {
				this.callFlexBr = true;
				this.debriefLog("Called for advanced airway cart");
			},
			notificationText: 'The VL/FBS is available for use.'					
		};

		this.neutralState.config.part7Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			waitForPreOxygenateVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), //'DAARC - Scenario9 - intro - 53',
			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection);
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						break;
					case 1:
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLog("Pre-Oxygenated with BVM. This may not be the best solution");			
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_'
				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			],
			onApneaStartedVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // APNEA TIMER INPUT SEGMENT CHANGE HERE,
			onPatientFullyMedicated: () => {	
				this.debriefSetAdministeringText("You correctly medicated your patient and earned maximum points.")
			},
			onSingleMedAdministered: () => {
				this.debriefSetAdministeringText("Did you administer all medications that are appropriate, given the patient's condition?") //May need more description			
			}
		};
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupSga();
		this._setupCric();
		this._setupAuscultate();
	}

	public getDefaultSa02() {
		return 92
	}

	public getDefaultApneaTime() {
		return 210;
	}

	private _setupBvm() {
		this.procedures.bvm.config.part1Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onChooseHandNextClicked: (isOneHanded: boolean) => {
				if(isOneHanded) {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
				} else {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
				}
			}
		};
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			onProcedureAttempted: (attemptCount: number) => {
				this.bvmAttemptCount = attemptCount
			}
		}
	}	

	private _setupEtt() {
		this.procedures.ett.config.isAttemptSuccessful = () => {
			return this.eTTCorrectOne && this.eTTCorrectTwo && this.eTTCorrectThree;
		}
		
		this.procedures.ett.config.part1Config = {			
			hideVlButton: () => !this.callFlexBr, // If Neutral step 6 has not been performed, the VL button should be hidden
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),//'DAARC - Scenario9 - intro - 53',
			insertingETTVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),//'DAARC - Scenario9 - intro - 53',
			ettSpecialUIConfig: {
				airwayImage: () => {
					if(this.ettDL) {
						if(this.eTTCorrectTwo) {
							return './assets/images/ett/Grade4.png'; //DL & Suction << appears after suction
						} else {
							return './assets/images/ett/Grade4Mucus.png'; // DL & No Suction << opens to this
						} 
					} else if(this.ettVL) {
						if(this.eTTCorrectTwo) {
							return './assets/images/ett/Grade2.png'; //VL & Suction << appears after suction
						} else {
							return './assets/images/ett/Grade2Mucus.png'; // VL & No Suction << opens to this
						}
					} else{
						return './assets/images/ett/Grade4Mucus.png'; // Default failsafe
					}	
				},
				magillForceps: {
					text: () => 'There is no foreign body in the patient’s airway.',					
				},
				suction: {
					text: () => '',
					onClick: () => {
						this.eTTCorrectTwo = true;
					}
				},
				manipulateLarynx: {
					text: () => 'It is difficult to locate or palpate thyroid and cricoid cartilage.'
				}
			},
			onEttSizeSelected: (sizeSelected : string) => {
				if(sizeSelected === '7' || sizeSelected === '8') {
					this.eTTCorrectThree = true;
				} else {
					this.eTTCorrectThree = false;
				}
			},
			onMakeAttemptClicked: (isDLClicked: boolean, isVLClicked: boolean) => {
				if(isDLClicked){
					this.ettDL = true;
					this.ettVL = false;
				} else if(isVLClicked) { // VL shouldbe selected for the answer to be correct
					this.ettDL = false;
					this.ettVL = true;
				} else { // it should not be possible to select neither, just a fail safe
					this.ettDL = false;
					this.ettVL = false;
				}
				this.eTTCorrectOne = isVLClicked;
			},
			onProcedureAttempted: (attemptCount: number) => {							
				this.ettAttemptCount = attemptCount;
			}

		};
	}

	private _setupSga() {
		this.procedures.sga.config.part1Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_SGAAttempt()),//'DAARC - Scenario9 - intro - 53',
			insertingSgaVideoChapter: () => this._id_SegmentAssembly(this._id_SGAAttempt()),//'DAARC - Scenario9 - intro - 53',
			onProcedureAttempted: (attemptCount: number) => {
				this.sgaAttemptCount = attemptCount
			}
		};
	}

	private _setupCric() {
		this.procedures.cric.config.isAttemptSuccessful = () => {
			return true;
		};

		this.procedures.cric.config.part1Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			correctToolOrder: [
				'Scalpel',
				'Spreader',
				'Bougie',
				'ETT',
				'Syringe',
			]
		};
		this.procedures.cric.config.part2Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			identifyLandmarksVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(1), 'flat'),//'DAARC - Scenario9 - intro - 53'
		};
		this.procedures.cric.config.part3Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4'
		};
		this.procedures.cric.config.part4Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			scalpelVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(2), 'flat'),//'DAARC - Scenario9 - intro - 53',
			onMakingIncition: () => { //daarc lite ends when the incision is ready to be made
				this.cricAttemptCount++;
			}	
		};
		this.procedures.cric.config.part5Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			spreaderVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(3), 'flat')//'DAARC - Scenario9 - intro - 53'
		};
		this.procedures.cric.config.part6Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			bougieVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(4), 'flat'),//'DAARC - Scenario9 - intro - 53'
		};
		this.procedures.cric.config.part7Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			etTubeVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(5), 'flat'),//'DAARC - Scenario9 - intro - 53'
		};
		this.procedures.cric.config.part8Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(6), 'Flat'),//'DAARC - Scenario9 - intro - 53'
		};
		//part 9 here
	}

	private _setupAuscultate() {
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario9/daarc-scenario9-ruthmckenzie-v1.mp4',
			bvmConfig: {
                moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc),//'DAARC - Scenario9 - intro - 53',
                checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathGurgle;
				}
            },
            ettConfig: {
                moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc),//'DAARC - Scenario9 - intro - 53',
                checkRiseAndTubeHumidificationText: () => {
					if(this.eTTCorrectOne && this.eTTCorrectTwo && this.eTTCorrectThree) {
						return this.auscSuccess;
					} else{
						return this.auscFailed;
					}
				},
				audioPath: () => {
					if(this.eTTCorrectOne && this.eTTCorrectTwo && this.eTTCorrectThree){
						return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
					} else {
						return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
					}
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					if(this.eTTCorrectOne && this.eTTCorrectTwo && this.eTTCorrectThree) {
						return isYesClicked
					} else return !isYesClicked					
				},
                auscultateHoverOverMessages:() => { //tummy gurgling
					if(this.eTTCorrectOne && this.eTTCorrectTwo && this.eTTCorrectThree){
						return this.hoverBreathYes
					} else {
						return this.hoverBreathGurgle
					}
					
				}
            },
            sgaConfig: {
                moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc),//'DAARC - Scenario9 - intro - 53',
                checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathGurgle
				}
            },
            cricConfig: {
                moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_cricAusc, 'flat'),//'DAARC - Scenario9 - intro - 53',
                checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked
				},
                auscultateHoverOverMessages:() => {
					return this.hoverBreathYes
				}
			}
		};
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}
}